import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchCrmStats, isCrmDisabledError, isCrmViewBlockedError } from '../api/crmCustomers';
import { fetchAssignableUsers } from '../api/userManagement';
import {
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_TYPE_OPTIONS,
  formatAssignableUserLabel,
} from './customerDetail/customerDetailUtils';
import { formatDate, formatDateTime, formatEnumLabel, formatValue } from '../utils/crmFormatting';
import { pageColors } from './HomePage';
import CrmDashboardBlockedState from './crmDashboard/CrmDashboardBlockedState';

const TENANT_WIDE_ROLES = new Set(['TENANT_ADMIN', 'SUPER_ADMIN']);

const CRM_STATS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.crmStats || pageColors.crm,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(0,121,107,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const SELECT_FIELD_SX = {
  width: { xs: '100%', sm: 220 },
  flex: { sm: '0 0 auto' },
};

const ASSIGNEE_FIELD_SX = {
  width: { xs: '100%', sm: 320 },
  flex: { sm: '0 0 auto' },
};

const DATE_FIELD_SX = {
  width: { xs: '100%', sm: 190 },
  flex: { sm: '0 0 auto' },
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

const STAT_ITEMS = [
  { key: 'total', label: 'Total' },
  { key: 'open', label: 'OPEN' },
  { key: 'overdue', label: 'OPEN (overdue)' },
  { key: 'done', label: 'DONE' },
  { key: 'doneOverdue', label: 'DONE (overdue)' },
  { key: 'cancelled', label: 'CANCELLED' },
];

const ACTIVITY_TYPE_LABELS = new Map(
  ACTIVITY_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

const STATE_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const SORT_COLUMNS = [
  { id: 'customer', label: 'Customer', width: '18%' },
  { id: 'title', label: 'Title', width: '22%' },
  { id: 'activityType', label: 'Type', width: '11%' },
  { id: 'state', label: 'State', width: '11%' },
  { id: 'assignee', label: 'Assignee', width: '14%' },
  { id: 'createdAt', label: 'Created date', width: '13%' },
  { id: 'dueDate', label: 'Due date', width: '11%' },
  { id: 'completedAt', label: 'Completed date', width: '13%' },
];

const INITIAL_FILTERS = {
  assigneeId: '',
  createdFrom: '',
  createdTo: '',
  dueFrom: '',
  dueTo: '',
  activityType: '',
  state: '',
};

const EMPTY_STATS = {
  total: 0,
  open: 0,
  done: 0,
  cancelled: 0,
  overdue: 0,
  doneOverdue: 0,
};

function getActivityTypeLabel(value) {
  const normalized = String(value ?? '').trim();
  return ACTIVITY_TYPE_LABELS.get(normalized) || formatEnumLabel(normalized, 'Not set');
}

function getStateLabel(value) {
  const normalized = String(value ?? '').trim();
  return ACTIVITY_STATUS_LABELS[normalized] || formatEnumLabel(normalized, 'Not set');
}

function normalizeString(value) {
  return String(value ?? '').trim();
}

function normalizeTimestamp(value) {
  const normalized = normalizeString(value);
  const timestamp = normalized ? Date.parse(normalized) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
}

function buildRow(activity, index) {
  const id = Number(activity?.id ?? 0);
  const customerNumber = normalizeString(activity?.customerNumber);
  const customerName = normalizeString(activity?.customerName);
  const customerDisplay = customerName || customerNumber || 'Not available';
  const activityTypeLabel = getActivityTypeLabel(activity?.activityType);
  const stateLabel = getStateLabel(activity?.state);
  const assigneeLabel = normalizeString(activity?.assignedUser?.displayName);
  const createdAtRaw = normalizeString(activity?.createdAt);
  const dueDateRaw = normalizeString(activity?.dueDate);
  const completedAtRaw = normalizeString(activity?.completedAt);

  return {
    id,
    key: id || `${customerNumber}-${normalizeString(activity?.title)}-${index}`,
    customerNumber,
    customerDisplay,
    customerHref: customerNumber ? `/customers/${encodeURIComponent(customerNumber)}` : '',
    titleDisplay: formatValue(activity?.title, 'Untitled activity'),
    activityTypeLabel,
    stateLabel,
    assigneeLabel: assigneeLabel || 'Unassigned',
    createdAtDisplay: formatDateTime(createdAtRaw, 'Not recorded'),
    dueDateDisplay: formatDate(dueDateRaw, 'No due date'),
    completedAtDisplay: formatDateTime(completedAtRaw, 'Not recorded'),
    sortValues: {
      customer: customerName || customerNumber,
      title: normalizeString(activity?.title),
      activityType: activityTypeLabel,
      state: stateLabel,
      assignee: assigneeLabel,
      createdAt: normalizeTimestamp(createdAtRaw),
      dueDate: normalizeTimestamp(dueDateRaw),
      completedAt: normalizeTimestamp(completedAtRaw),
    },
  };
}

function isBlankSortValue(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

function compareSortValues(leftValue, rightValue, order) {
  const leftBlank = isBlankSortValue(leftValue);
  const rightBlank = isBlankSortValue(rightValue);

  if (leftBlank && rightBlank) {
    return 0;
  }

  if (leftBlank) {
    return 1;
  }

  if (rightBlank) {
    return -1;
  }

  const comparison =
    typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue), undefined, {
          sensitivity: 'base',
          numeric: true,
        });

  return order === 'asc' ? comparison : -comparison;
}

function sortRows(rows, orderBy, order) {
  return [...rows].sort((left, right) => {
    const valueComparison = compareSortValues(
      left.sortValues[orderBy],
      right.sortValues[orderBy],
      order
    );

    if (valueComparison !== 0) {
      return valueComparison;
    }

    return Number(right.id ?? 0) - Number(left.id ?? 0);
  });
}

function buildStatsResponse(data) {
  return {
    today: normalizeString(data?.today),
    scope: normalizeString(data?.scope),
    stats: {
      ...EMPTY_STATS,
      ...(data?.stats || {}),
    },
    activities: Array.isArray(data?.activities) ? data.activities : [],
  };
}

const CrmStatsResultsTable = memo(function CrmStatsResultsTable({
  emptyMessage,
  onRequestSort,
  order,
  orderBy,
  rows,
}) {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...SURFACE_SX,
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }} aria-label="CRM stats table">
        <TableHead>
          <TableRow>
            {SORT_COLUMNS.map((column) => (
              <TableCell key={column.id} sx={{ ...TABLE_HEADER_SX, width: column.width }}>
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : 'asc'}
                  onClick={() => onRequestSort(column.id)}
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={SORT_COLUMNS.length} align="center" sx={{ py: 5 }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.key} hover sx={TABLE_ROW_HOVER_SX}>
                <TableCell>
                  {row.customerHref ? (
                    <Box
                      component={Link}
                      to={row.customerHref}
                      sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none' }}
                    >
                      {row.customerDisplay}
                    </Box>
                  ) : (
                    row.customerDisplay
                  )}
                </TableCell>
                <TableCell>{row.titleDisplay}</TableCell>
                <TableCell>{row.activityTypeLabel}</TableCell>
                <TableCell>{row.stateLabel}</TableCell>
                <TableCell>{row.assigneeLabel}</TableCell>
                <TableCell>{row.createdAtDisplay}</TableCell>
                <TableCell>{row.dueDateDisplay}</TableCell>
                <TableCell>{row.completedAtDisplay}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

export default function CrmStatsPage({ connected, currentUser, setConnected, setError }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableUsersError, setAssignableUsersError] = useState('');
  const [isLoadingAssignableUsers, setIsLoadingAssignableUsers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [result, setResult] = useState(buildStatsResponse({}));
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('createdAt');
  const requestRef = useRef(0);
  const navigate = useNavigate();
  const isTenantWideView = TENANT_WIDE_ROLES.has(currentUser?.role);

  useEffect(() => {
    if (!isTenantWideView || !connected) {
      setAssignableUsers([]);
      setAssignableUsersError('');
      setFilters((currentFilters) => ({ ...currentFilters, assigneeId: '' }));
      return undefined;
    }

    let isActive = true;
    setIsLoadingAssignableUsers(true);
    setAssignableUsersError('');

    fetchAssignableUsers()
      .then((users) => {
        if (!isActive) {
          return;
        }

        setAssignableUsers(users);
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }

        console.error('Failed to load assignable CRM users:', error);
        setAssignableUsersError(error.message || 'Failed to load assignable users.');
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingAssignableUsers(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [connected, isTenantWideView]);

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
  };

  const buildRequestFilters = useCallback(() => {
    const requestFilters = {
      createdFrom: filters.createdFrom,
      createdTo: filters.createdTo,
      dueFrom: filters.dueFrom,
      dueTo: filters.dueTo,
      activityType: filters.activityType,
      state: filters.state,
    };

    if (isTenantWideView) {
      requestFilters.assigneeId = filters.assigneeId;
    }

    return requestFilters;
  }, [filters, isTenantWideView]);

  const handleSearch = async () => {
    if (!connected) {
      setConnected(false);
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsLoading(true);
    setLoadError('');

    try {
      const data = await fetchCrmStats(buildRequestFilters());

      if (requestRef.current !== requestId) {
        return;
      }

      setResult(buildStatsResponse(data));
      setHasSearched(true);
      setOrderBy('createdAt');
      setOrder('desc');
    } catch (error) {
      if (requestRef.current !== requestId) {
        return;
      }

      console.error('Failed to load CRM stats:', error);

      if (isCrmDisabledError(error)) {
        setError(error.message || 'CRM is disabled for this tenant');
        navigate('/', { replace: true });
        return;
      }

      if (isCrmViewBlockedError(error)) {
        setConnected(false);
        return;
      }

      setLoadError(error.message || 'Failed to load CRM stats.');
    } finally {
      if (requestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  const handleRequestSort = useCallback((property) => {
    setOrderBy((currentOrderBy) => {
      if (currentOrderBy !== property) {
        setOrder('asc');
        return property;
      }

      setOrder((currentOrder) => (currentOrder === 'asc' ? 'desc' : 'asc'));
      return currentOrderBy;
    });
  }, []);

  const rows = useMemo(
    () => result.activities.map((activity, index) => buildRow(activity, index)),
    [result.activities]
  );

  const sortedRows = useMemo(() => sortRows(rows, orderBy, order), [order, orderBy, rows]);

  const tableEmptyMessage = hasSearched
    ? 'No CRM activities match the selected filters.'
    : 'Choose filters and search to view CRM activity stats.';

  if (!connected) {
    return (
      <CrmDashboardBlockedState
        isTenantWideView={isTenantWideView}
      />
    );
  }

  return (
    <ThemeProvider theme={CRM_STATS_THEME}>
      <Box sx={{ background: PAGE_BACKGROUND, minHeight: '100vh', py: { xs: 3, md: 4 } }}>
        <Container maxWidth="xl">
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: pageColors.crmStats || pageColors.crm,
                    color: '#fff',
                  }}
                >
                  <BarChartRoundedIcon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 2 }}>
                    CRM
                  </Typography>
                  <Typography variant="h5" fontWeight={800}>
                    CRM stats
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Search CRM activities by assignment, dates, type, and state.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'flex-start' }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stats and rows are loaded only when Search is clicked.
                    </Typography>
                  </Box>

                  {!isTenantWideView && (
                    <Alert severity="info" sx={{ py: 0, alignItems: 'center' }}>
                      Assigned to you
                    </Alert>
                  )}
                </Stack>

                {assignableUsersError && <Alert severity="warning">{assignableUsersError}</Alert>}
                {loadError && <Alert severity="error">{loadError}</Alert>}

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.25,
                    alignItems: 'center',
                  }}
                >
                  {isTenantWideView && (
                    <TextField
                      id="crm-stats-assignee"
                      select
                      label="Assigned to"
                      value={filters.assigneeId}
                      onChange={handleFilterChange('assigneeId')}
                      size="small"
                      sx={ASSIGNEE_FIELD_SX}
                      InputLabelProps={{ shrink: true }}
                      SelectProps={{ native: true }}
                      disabled={isLoadingAssignableUsers}
                    >
                      <option value="">All assignees</option>
                      {assignableUsers.map((user) => (
                        <option key={user.id} value={String(user.id)}>
                          {formatAssignableUserLabel(user)}
                        </option>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    label="Created from"
                    type="date"
                    value={filters.createdFrom}
                    onChange={handleFilterChange('createdFrom')}
                    size="small"
                    sx={DATE_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Created to"
                    type="date"
                    value={filters.createdTo}
                    onChange={handleFilterChange('createdTo')}
                    size="small"
                    sx={DATE_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Due from"
                    type="date"
                    value={filters.dueFrom}
                    onChange={handleFilterChange('dueFrom')}
                    size="small"
                    sx={DATE_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Due to"
                    type="date"
                    value={filters.dueTo}
                    onChange={handleFilterChange('dueTo')}
                    size="small"
                    sx={DATE_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    id="crm-stats-activity-type"
                    select
                    label="Activity type"
                    value={filters.activityType}
                    onChange={handleFilterChange('activityType')}
                    size="small"
                    sx={SELECT_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All types</option>
                    {ACTIVITY_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField
                    id="crm-stats-state"
                    select
                    label="State"
                    value={filters.state}
                    onChange={handleFilterChange('state')}
                    size="small"
                    sx={SELECT_FIELD_SX}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All states</option>
                    {STATE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <Button
                    type="button"
                    variant="contained"
                    startIcon={<SearchRoundedIcon fontSize="small" />}
                    onClick={handleSearch}
                    disabled={isLoading}
                    sx={{ height: 38, px: 2, textTransform: 'none' }}
                  >
                    {isLoading ? 'Searching...' : 'Search'}
                  </Button>
                </Box>
              </Stack>
            </Paper>

            {hasSearched && (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      lg: 'repeat(6, minmax(0, 1fr))',
                    },
                    gap: 1,
                  }}
                >
                  {STAT_ITEMS.map((item) => (
                    <Box
                      key={item.key}
                      aria-label={`${item.label} ${Number(result.stats[item.key] ?? 0)}`}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        backgroundColor: 'rgba(0,121,107,0.06)',
                        border: '1px solid rgba(0,121,107,0.12)',
                      }}
                    >
                      <Typography variant="caption" fontWeight={800} color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={800}>
                        {Number(result.stats[item.key] ?? 0).toLocaleString()}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {isLoading && <LinearProgress />}

            <CrmStatsResultsTable
              emptyMessage={tableEmptyMessage}
              onRequestSort={handleRequestSort}
              order={order}
              orderBy={orderBy}
              rows={sortedRows}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
